import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  useCreateReviewActionItem,
  useDeleteReviewActionItem,
  useUpdateReviewActionItem,
} from '../queries';
import type { ReviewActionItem } from '../types';

interface Props {
  reviewId: number;
  items: ReviewActionItem[];
}

/** Detail-page checklist of follow-up action items coming out of a review. */
export function ReviewActionItems({ reviewId, items }: Props) {
  const [newItem, setNewItem] = useState('');
  const add = useCreateReviewActionItem();
  const toggle = useUpdateReviewActionItem();
  const remove = useDeleteReviewActionItem();

  const sorted = [...items].sort((a, b) => a.order - b.order || a.id - b.id);
  const done = sorted.filter((i) => i.is_done).length;
  const pct = sorted.length > 0 ? Math.round((done / sorted.length) * 100) : 0;

  async function submitNew(e: React.FormEvent) {
    e.preventDefault();
    const title = newItem.trim();
    if (!title) return;
    await add.mutateAsync({ review: reviewId, title, order: sorted.length + 1 });
    setNewItem('');
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Action items</CardTitle>
        <CardDescription>
          {done}/{sorted.length} done · {pct}%
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="bg-muted relative h-1.5 w-full overflow-hidden rounded-full">
          <div className="bg-primary h-full transition-all" style={{ width: `${pct}%` }} />
        </div>
        <ul className="space-y-1">
          {sorted.length === 0 ? (
            <li className="text-muted-foreground py-2 text-sm">No action items yet.</li>
          ) : (
            sorted.map((item) => (
              <li
                key={item.id}
                className="hover:bg-muted/40 flex items-center gap-2 rounded px-2 py-1.5"
              >
                <input
                  id={`review-item-${item.id}`}
                  type="checkbox"
                  checked={item.is_done}
                  onChange={(e) =>
                    toggle.mutate({ id: item.id, input: { is_done: e.target.checked } })
                  }
                  className="accent-primary size-4"
                  aria-label={`Mark "${item.title}" done`}
                />
                <label
                  htmlFor={`review-item-${item.id}`}
                  className={cn(
                    'flex-1 text-sm',
                    item.is_done && 'text-muted-foreground line-through',
                  )}
                >
                  {item.title}
                </label>
                {item.due_date ? (
                  <span className="text-muted-foreground text-xs">
                    Due {new Date(item.due_date).toLocaleDateString()}
                  </span>
                ) : null}
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={`Remove ${item.title}`}
                  onClick={() => remove.mutate(item.id)}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </li>
            ))
          )}
        </ul>
        <form onSubmit={submitNew} className="flex gap-2">
          <Input
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            placeholder="Add an action item…"
            aria-label="New action item"
          />
          <Button type="submit" size="sm" disabled={!newItem.trim() || add.isPending}>
            <Plus className="size-4" /> Add
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
